import React, { useCallback, useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Save,
  CheckCircle
} from "lucide-react";

import {
  getStandaloneEntryPortal,
  saveStandaloneEntryPortal,
  submitStandaloneEntryPortal
} from "../../api/markschedule/standaloneMarksPortalApi";
import MarksEntryTable from "../../components/markschedule/MarksEntryTable";
import AssignmentStatusBadge from "../../components/markschedule/AssignmentStatusBadge";
import {
  buildMarksSavePayload,
  canEditMarks,
  pickList
} from "../../utils/markschedule/marksHelpers";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const StandaloneMarksEntryPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [rows, setRows] = useState([]);
  const [maxMarks, setMaxMarks] = useState(0);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getStandaloneEntryPortal(assignmentId);
      setAssignment(res.assignment || null);
      setMaxMarks(res.maxMarks || res.assignment?.maxMarks || 0);
      setLocked(Boolean(res.locked));
      setRows(
        pickList(res, ["rows", "data"]).map((r) => ({
          ...r,
          marksObtained: r.marksObtained,
          isAbsent: Boolean(r.isAbsent),
        }))
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSubmitting(true);
      await saveStandaloneEntryPortal(
        buildMarksSavePayload(assignmentId, rows)
      );
      toast.success("Draft saved");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm("Submit and lock marks?")) return;
    try {
      setSubmitting(true);
      await saveStandaloneEntryPortal(
        buildMarksSavePayload(assignmentId, rows)
      );
      await submitStandaloneEntryPortal(assignmentId);
      toast.success("Submitted and locked");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <Skeleton height={24} width="120px" />
        <Skeleton height={80} width="100%" />
        <Skeleton height={300} width="100%" />
      </div>
    );
  }

  const editable = assignment && canEditMarks(assignment) && !locked;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Back Button */}
      <button
        type="button"
        onClick={() =>
          navigate("/staff-panel/marks/standalone/my-assignments")
        }
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> My Assignments
      </button>

      {/* Assignment Header Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {assignment?.schemeName ||
                assignment?.schemeId?.name ||
                "Marks Entry"}
            </CardTitle>
            <AssignmentStatusBadge status={assignment?.status} />
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            {assignment?.classId?.className} · {assignment?.sectionId?.sectionName}
            {maxMarks ? ` · Max ${maxMarks}` : ""}
          </p>
        </CardHeader>
      </Card>

      {/* Alerts */}
      
      

      {/* Marks Table */}
      <MarksEntryTable
        rows={rows}
        maxMarks={maxMarks}
        locked={!editable}
        onChange={setRows}
      />

      {/* Actions Bar */}
      {editable ? (
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="secondary"
            isLoading={submitting}
            onClick={handleSave}
            leftIcon={<Save className="h-4 w-4" />}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit}
            leftIcon={<CheckCircle className="h-4 w-4" />}
          >
            Submit &amp; Lock
          </Button>
        </div>
      ) : (
        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
          Read-only — assignment is {assignment?.status || "locked"}.
        </p>
      )}
    </div>
  );
};

export default StandaloneMarksEntryPage;